"use client";

import type { JSX } from "react";
import { Button, Text, View } from "@/components";
import { useLayout } from "@/providers";
import type { JobCardProps } from "@/types";

/**
 * @name JobCard
 * @description Card component to display job information.
 * The component displays a job card with a header, tags, description, meta info, and action footer.
 *
 * @param {JobCardProps} props - The properties object containing all job details.
 * @returns {JSX.Element} The rendered Job Card component.
 *
 * @example
 * ```tsx
 * // Example usage of JobCard
 * <JobCard
 *   id="1"
 *   title="Frontend Dev"
 *   salaryRange="$50k - $80k"
 *   tags={["React", "TS"]}
 *   description="Awesome job"
 *   meta={{ expiry: "2 days", location: "Remote", level: "Mid", proposals: 10 }}
 *   company={{ name: "Tech Co", logo: "", verified: true }}
 * />
 * ```
 */
const JobCard = ({
  title,
  tags,
  description,
  meta
}: JobCardProps): JSX.Element => {
  const { t } = useLayout();

  return (
    <View className="block box p-6 rounded-3xl bg-card border border-transparent shadow-sm hover:shadow-md transition-all">
      <View type="article" className="flex flex-col h-full">
        <View type="header" className="mb-4">
          <Text
            type="h4"
            className="text-xl font-bold text-card-foreground mb-3"
          >
            {title}
          </Text>
          <View className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Text
                key={tag}
                className="text-xs font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-full"
              >
                {tag}
              </Text>
            ))}
          </View>
        </View>

        <Text
          type="p"
          className="text-muted-foreground text-sm mb-6 line-clamp-3 leading-relaxed"
        >
          {description}
        </Text>

        <View
          type="ul"
          className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6 text-sm text-muted-foreground font-medium list-none"
        >
          <View
            type="li"
            className="flex items-center gap-1.5 whitespace-nowrap"
          >
            <Text>
              {t("expires")}: {meta.expiry}
            </Text>
          </View>
          <View
            type="li"
            className="flex items-center gap-1.5 whitespace-nowrap"
          >
            <View className="w-1 h-1 rounded-full bg-muted-foreground" />
            <Text>
              {t("location")}: {meta.location}
            </Text>
          </View>
          <View
            type="li"
            className="flex items-center gap-1.5 whitespace-nowrap"
          >
            <View className="w-1 h-1 rounded-full bg-muted-foreground" />
            <Text>
              {t("level")}: {meta.level}
            </Text>
          </View>
          <View
            type="li"
            className="flex items-center gap-1.5 whitespace-nowrap"
          >
            <View className="w-1 h-1 rounded-full bg-muted-foreground" />
            <Text>
              {t("proposals")}: {meta.proposals}
            </Text>
          </View>
        </View>

        <View className="mt-auto">
          <Button
            className="w-auto px-6 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full text-sm font-semibold transition-colors shadow-lg active:scale-95"
            type="button"
          >
            {t("apply")}
          </Button>
        </View>
      </View>
    </View>
  );
};

export default JobCard;
